import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Group,
  Loader,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core'
import {
  Album,
  AlertCircle,
  Check,
  ChevronRight,
  Info,
  RefreshCcw,
} from 'lucide-react'
import { format } from 'date-fns'
import { useCaseCreationStore } from '@/store/useCaseCreationStore'
import { useDispatcher } from '@/providers/DispatcherProvider'
import { useDispatchTriage } from '@/hooks/api/useDispatchTriage'
import { useDebounce } from '@/hooks/useDebounce'
import type {
  AmbulanceRequestInformationFormValues,
  CallerInformationFormValues,
  DispatchTriageStageId,
} from '@/types/cases'
import type {
  DispatchTriageRequest,
  DispatchTriageStageData,
  TriageSeverity,
} from '@/types/api/triage'

type StageStatus = 'idle' | 'waiting' | 'loading' | 'success' | 'error'

interface StageRuntime {
  status: StageStatus
  data?: DispatchTriageStageData
  error?: string | null
  lastHash?: string
}

type StageRuntimeMap = Partial<Record<DispatchTriageStageId, StageRuntime>>

interface StageBuilderState {
  dispatcherName?: string
  callerInformation?: Partial<CallerInformationFormValues> | null
  ambulanceInformation?: Partial<AmbulanceRequestInformationFormValues> | null
  currentSection?: 'caller-information' | 'ambulance-request-information' | null
}

interface StagePayloadOptions {
  userInput?: string
}

interface StageConfig {
  id: DispatchTriageStageId
  label: string
  description: string
  autoTrigger: boolean
  dependsOn?: DispatchTriageStageId[]
  requirement: (state: StageBuilderState) => boolean
  waitingMessage?: (state: StageBuilderState) => string | undefined
  buildPayload: (
    state: StageBuilderState,
    options?: StagePayloadOptions
  ) => DispatchTriageRequest | null
}

interface StageItem {
  config: StageConfig
  runtime: StageRuntime
  waitingCopy?: string
}

const DEFAULT_DISPATCH_TIPS = [
  'Listen carefully and stay calm while guiding the caller.',
  'Assess quickly if the call is legitimate or a prank.',
  'Ask clarifying questions to understand the real emergency.',
]

const CALL_TYPE_LABEL: Record<string, string> = {
  undetermined: 'Undetermined',
  general_inquiry: 'General Inquiry',
  emergency: 'Emergency',
  ambulance_request: 'Ambulance Request',
}

const CALL_CATEGORY_LABEL: Record<string, string> = {
  'undetermined/accidental-call': 'Accidental call',
  'undetermined/dropped-call': 'Dropped call',
  'undetermined/irrelevant-call': 'Irrelevant call',
  'general-inquiry/service-information': 'Service information',
  'general-inquiry/pricing-request': 'Pricing request',
  'general-inquiry/follow-up': 'Follow up',
  'emergency/dispatch-first-responder': 'Dispatch first responder',
  'ambulance-request/dispatch-era-ambulance': 'Dispatch ERA ambulance',
  'ambulance-request/dispatch-3rd-party-ambulance-partner':
    'Dispatch partner ambulance',
  'ambulance-request/interstate-transfer': 'Interstate transfer',
  'ambulance-request/interstate-hospital-transfer':
    'Interstate hospital transfer',
}

const STAGE_CONFIG: StageConfig[] = [
  {
    id: 'stage_1_greeting',
    label: 'Greeting & Opening',
    description: 'Establish rapport and take control of the conversation.',
    autoTrigger: true,
    requirement: state => Boolean(state.dispatcherName),
    waitingMessage: () => 'Dispatcher profile is required to start triage.',
    buildPayload: state => {
      if (!state.dispatcherName) return null
      return {
        stageId: 'stage_1_greeting',
        userInput: '',
        context: buildStageContext('stage_1_greeting', state),
      }
    },
  },
  {
    id: 'stage_2_phone_confirmation',
    label: 'Phone Confirmation',
    description: 'Verify the best callback number for the caller.',
    autoTrigger: true,
    dependsOn: ['stage_1_greeting'],
    requirement: state =>
      Boolean(state.callerInformation?.phone_number && formatCallerName(state)),
    waitingMessage: state => {
      if (!state.callerInformation?.phone_number) {
        return 'Capture the caller’s phone number to proceed.'
      }
      if (!formatCallerName(state)) {
        return 'Capture caller first and last name to continue.'
      }
      return undefined
    },
    buildPayload: (state, options) => {
      const phone = state.callerInformation?.phone_number
      const name = formatCallerName(state)
      if (!phone || !name) return null
      return {
        stageId: 'stage_2_phone_confirmation',
        userInput:
          options?.userInput ??
          `Caller identified as ${name}. Phone number provided as ${phone}.`,
        context: buildStageContext('stage_2_phone_confirmation', state),
      }
    },
  },
  {
    id: 'stage_3_legitimacy_screening',
    label: 'Legitimacy Screening',
    description: 'Confirm that the call represents a genuine emergency.',
    autoTrigger: true,
    dependsOn: ['stage_2_phone_confirmation'],
    requirement: state =>
      Boolean(
        state.callerInformation?.call_type &&
          state.callerInformation?.call_category
      ),
    waitingMessage: state =>
      !state.callerInformation?.call_type ||
      !state.callerInformation?.call_category
        ? 'Select call type and category to continue screening.'
        : undefined,
    buildPayload: (state, options) => {
      const callType = state.callerInformation?.call_type
      const callCategory = state.callerInformation?.call_category
      if (!callType || !callCategory) return null
      const reason = deriveCallReason(state.callerInformation)
      return {
        stageId: 'stage_3_legitimacy_screening',
        userInput:
          options?.userInput ??
          `Caller reports: ${reason ?? 'Emergency needs'}.`,
        context: buildStageContext('stage_3_legitimacy_screening', state),
      }
    },
  },
  {
    id: 'stage_4_caller_details',
    label: 'Caller Details',
    description: 'Capture how the caller prefers to be addressed.',
    autoTrigger: true,
    dependsOn: ['stage_3_legitimacy_screening'],
    requirement: state =>
      state.currentSection === 'ambulance-request-information' &&
      Boolean(
        state.callerInformation?.prefix &&
          state.callerInformation?.first_name &&
          state.callerInformation?.last_name
      ),
    waitingMessage: state =>
      !state.callerInformation?.prefix
        ? 'Select caller prefix/title.'
        : 'Provide caller first and last name.',
    buildPayload: (state, options) => {
      const { callerInformation } = state
      if (
        !callerInformation?.prefix ||
        !callerInformation.first_name ||
        !callerInformation.last_name
      ) {
        return null
      }

      const details = [
        `Prefix: ${callerInformation.prefix.toUpperCase()}`,
        `Name: ${callerInformation.first_name} ${callerInformation.last_name}`,
        callerInformation.date_of_birth
          ? `DOB: ${formatDateString(callerInformation.date_of_birth)}`
          : undefined,
      ]
        .filter(Boolean)
        .join(', ')

      return {
        stageId: 'stage_4_caller_details',
        userInput: options?.userInput ?? details,
        context: buildStageContext('stage_4_caller_details', state),
      }
    },
  },
  {
    id: 'stage_5_location_capture',
    label: 'Location Capture',
    description: 'Obtain precise location and landmarks.',
    autoTrigger: true,
    dependsOn: ['stage_4_caller_details'],
    requirement: state =>
      state.currentSection === 'ambulance-request-information' &&
      Boolean(state.callerInformation?.location),
    waitingMessage: () => 'Provide caller location to continue.',
    buildPayload: (state, options) => {
      const location = state.callerInformation?.location
      if (!location) return null
      return {
        stageId: 'stage_5_location_capture',
        userInput:
          options?.userInput ??
          buildLocationSummary(
            state.callerInformation,
            state.ambulanceInformation
          ),
        context: buildStageContext('stage_5_location_capture', state),
      }
    },
  },
  {
    id: 'stage_7_immediate_threat_assessment',
    label: 'Immediate Threats',
    description: 'Assess consciousness, breathing, and bleeding.',
    autoTrigger: true,
    dependsOn: ['stage_5_location_capture'],
    requirement: state =>
      state.currentSection === 'ambulance-request-information' &&
      Boolean(
        state.ambulanceInformation?.is_conscious &&
          state.ambulanceInformation?.severity_level
      ),
    waitingMessage: state => {
      if (!state.ambulanceInformation?.is_conscious) {
        return 'Capture whether the patient is conscious.'
      }
      if (!state.ambulanceInformation?.severity_level) {
        return 'Select severity level to continue.'
      }
      return undefined
    },
    buildPayload: (state, options) => {
      if (
        !state.ambulanceInformation?.is_conscious ||
        !state.ambulanceInformation.severity_level
      ) {
        return null
      }

      const summary = buildThreatSummary(state.ambulanceInformation)
      return {
        stageId: 'stage_7_immediate_threat_assessment',
        userInput: options?.userInput ?? summary,
        context: buildStageContext(
          'stage_7_immediate_threat_assessment',
          state
        ),
      }
    },
  },
  {
    id: 'stage_8_dispatch_confirmation',
    label: 'Dispatch Confirmation',
    description: 'Confirm dispatch status and reassure caller.',
    autoTrigger: true,
    dependsOn: ['stage_7_immediate_threat_assessment'],
    requirement: state =>
      state.currentSection === 'ambulance-request-information',
    waitingMessage: () => undefined,
    buildPayload: (state, options) => ({
      stageId: 'stage_8_dispatch_confirmation',
      userInput:
        options?.userInput ??
        'Acknowledged threat status. Ready to dispatch immediate help.',
      context: buildStageContext('stage_8_dispatch_confirmation', state),
    }),
  },
  {
    id: 'stage_9_handover_preparation',
    label: 'Handover Preparation',
    description: 'Prepare caller for ambulance arrival.',
    autoTrigger: true,
    dependsOn: ['stage_8_dispatch_confirmation'],
    requirement: state =>
      state.currentSection === 'ambulance-request-information',
    waitingMessage: () => undefined,
    buildPayload: (state, options) => ({
      stageId: 'stage_9_handover_preparation',
      userInput:
        options?.userInput ??
        'Caller confirmed readiness for ambulance arrival and information handover.',
      context: buildStageContext('stage_9_handover_preparation', state),
    }),
  },
]

const STAGE_CONFIG_MAP = STAGE_CONFIG.reduce<
  Record<DispatchTriageStageId, StageConfig>
>(
  (acc, stage) => {
    acc[stage.id] = stage
    return acc
  },
  {} as Record<DispatchTriageStageId, StageConfig>
)

const severityColorMap: Record<TriageSeverity, string> = {
  LOW: 'gray',
  MEDIUM: 'yellow',
  HIGH: 'orange',
  CRITICAL: 'red',
}

export default function GuidedProtocolQuestions() {
  const {
    callerInformation,
    ambulanceRequestInformation,
    currentSection,
    hasCallerData,
  } = useCaseCreationStore()
  const { dispatcher } = useDispatcher()

  const dispatcherName = useMemo(() => {
    if (!dispatcher) return undefined
    return [dispatcher.first_name, dispatcher.last_name]
      .filter(Boolean)
      .join(' ')
      .trim()
  }, [dispatcher])

  const {
    stageItems,
    accordionValue,
    setAccordionValue,
    retryStage,
    isMutating,
    globalError,
  } = useGuidedProtocolQuestions({
    callerInformation,
    ambulanceInformation: ambulanceRequestInformation,
    dispatcherName,
    currentSection,
  })

  const instructionStage = stageItems.find(
    item => item.config.id === 'stage_1_greeting'
  )
  const instructionList = instructionStage?.runtime.data?.instructions?.length
    ? instructionStage.runtime.data.instructions
    : DEFAULT_DISPATCH_TIPS
  const showInstructionBlock =
    (instructionStage?.runtime.status || 'idle') !== 'success'

  const hasAnyStageData = stageItems.some(
    item => item.runtime.status === 'success'
  )
  const lastCompletedIndex = stageItems.reduce((acc, stage, index) => {
    return stage.runtime.status === 'success' ? index : acc
  }, -1)

  const resolvedActiveIndex = (() => {
    if (accordionValue) {
      const idx = stageItems.findIndex(
        item => item.config.id === accordionValue
      )
      if (idx >= 0) return idx
    }
    if (lastCompletedIndex >= 0) return lastCompletedIndex
    return 0
  })()

  const maxVisibleIndex = Math.min(
    Math.max(resolvedActiveIndex, lastCompletedIndex) + 1,
    stageItems.length - 1
  )

  const visibleStages = stageItems.filter((_, idx) => idx <= maxVisibleIndex)

  return (
    <div className="flex flex-col border border-light-gray-20 rounded-md bg-white min-w-[551px] w-full h-[min(610px,55vh)] min-h-[280px] overflow-hidden">
      <div className="border-b border-light-gray-20 px-4 py-3 flex items-center justify-between">
        <Group gap="xs">
          <Album color="#254769" width={20} height={20} />
          <div>
            <Text size="md" fw={600} c="grey.4">
              Guided Protocol Questions
            </Text>
            <Text size="xs" c="gray.6">
              Auto-populated scripts based on caller inputs
            </Text>
          </div>
        </Group>
        {isMutating && <Loader size="sm" color="blue" />}
      </div>

      {showInstructionBlock && (
        <div className="px-4 py-3 border-b border-light-gray-20 bg-[#F9FAFB]">
          <Group gap="xs" align="flex-start">
            <Info
              width={16}
              height={16}
              className="text-primary mt-1 shrink-0"
            />
            <div className="space-y-2">
              <Text size="sm" fw={600} c="grey.4">
                Dispatch Instructions
              </Text>
              <ul className="list-disc pl-5 text-xs text-gray-600 space-y-1">
                {instructionList.map(instruction => (
                  <li key={instruction}>{instruction}</li>
                ))}
              </ul>
            </div>
          </Group>
        </div>
      )}

      {globalError && (
        <div className="px-4 pt-3">
          <Alert
            color="red"
            icon={<AlertCircle size={16} />}
            title="We ran into an issue"
            variant="light"
          >
            {globalError}
          </Alert>
        </div>
      )}

      <div className="flex-1 overflow-hidden px-4 pb-4">
        <div className="h-full overflow-y-auto pr-1">
          {hasCallerData() ? (
            <Accordion
              multiple={false}
              value={accordionValue}
              onChange={value =>
                setAccordionValue(
                  (value as DispatchTriageStageId | null) ?? null
                )
              }
              chevron={null}
              radius="md"
            >
              {visibleStages.map((stage, index) => (
                <Accordion.Item
                  value={stage.config.id}
                  key={stage.config.id}
                  className="mb-3 border-none"
                >
                  <StageAccordionControl
                    stage={stage}
                    index={index}
                    isActive={accordionValue === stage.config.id}
                  />
                  <Accordion.Panel>
                    <StagePanelContent stage={stage} retryStage={retryStage} />
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          ) : (
            <EmptyState dispatcherName={dispatcherName} />
          )}
          {!hasAnyStageData && hasCallerData() && (
            <Skeleton height={120} mt="lg" radius="md" />
          )}
        </div>
      </div>
    </div>
  )
}

function StageAccordionControl({
  stage,
  index,
  isActive,
}: {
  stage: StageItem
  index: number
  isActive: boolean
}) {
  const isCompleted = stage.runtime.status === 'success'
  const indicatorClasses = isCompleted
    ? 'bg-[#254769] text-white border-[#254769]'
    : isActive
      ? 'border-[#254769] text-[#254769]'
      : 'border-gray-300 text-gray-500'

  const containerClasses = isActive
    ? 'bg-[#E9F2FF] border-[#A8C5FF]'
    : 'bg-white border-[#E1E4E8]'

  return (
    <Accordion.Control
      chevron={null}
      className={`rounded-lg border px-4 py-3 ${containerClasses}`}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="sm" align="center" wrap="nowrap">
          <div
            className={`w-8 h-8 rounded-md border flex items-center justify-center text-xs font-semibold ${indicatorClasses}`}
          >
            {isCompleted ? <Check size={13} /> : index + 1}
          </div>
          <div className="min-w-0">
            <Text
              fw={600}
              size="sm"
              c={isActive ? '#254769' : 'grey.7'}
              className="truncate"
            >
              {stage.config.label}
            </Text>
            <Text size="xs" c="gray.6" className="truncate">
              {stage.config.description}
            </Text>
          </div>
        </Group>
        {stage.runtime.status === 'loading' && <Loader size="xs" />}
      </Group>
    </Accordion.Control>
  )
}

function StagePanelContent({
  stage,
  retryStage,
}: {
  stage: StageItem
  retryStage: (stageId: DispatchTriageStageId) => void
}) {
  const { runtime, config, waitingCopy } = stage

  if (runtime.status === 'loading') {
    return <Skeleton height={100} radius="md" />
  }

  if (runtime.status === 'error') {
    return (
      <Alert
        color="red"
        icon={<AlertCircle size={16} />}
        title="Unable to load step"
        variant="light"
        className="mb-3"
      >
        {runtime.error}
        <div className="mt-2">
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={() => retryStage(config.id)}
          >
            <RefreshCcw size={16} />
          </ActionIcon>
        </div>
      </Alert>
    )
  }

  if (runtime.status !== 'success' || !runtime.data) {
    return (
      <div className="text-xs text-gray-600 bg-gray-50 border border-dashed border-gray-200 rounded-md p-3">
        {waitingCopy ??
          'Awaiting required information to auto-generate script.'}
      </div>
    )
  }

  const data = runtime.data
  const nextStageId =
    data.next_stage || data.nextStage || getNextStageId(config.id)
  const nextStageLabel = nextStageId
    ? (STAGE_CONFIG_MAP[nextStageId]?.label ?? nextStageId)
    : null

  return (
    <Stack gap="sm">
      <div className="bg-[#F7FAFF] border border-[#E0E7FF] rounded-md p-3 space-y-2">
        <Text size="sm" fw={600} c="#1E3A8A">
          AI Script
        </Text>
        <Text size="sm" c="gray.7">
          {data.script}
        </Text>
        <Group gap="xs" wrap="wrap">
          <Badge color={severityColorMap[data.severity]} variant="filled">
            {data.severity}
          </Badge>
          {data.provisional_diagnosis && (
            <Badge color="blue" variant="light">
              {data.provisional_diagnosis}
            </Badge>
          )}
          {data.timestamp && (
            <Text size="xs" c="gray.5">
              {format(new Date(data.timestamp), 'MMM d, yyyy • h:mm:ss a')}
            </Text>
          )}
        </Group>
      </div>

      {data.action_taken?.length > 0 && (
        <div>
          <Text size="xs" fw={600} c="gray.7" className="mb-1">
            Action Taken
          </Text>
          <ul className="list-disc pl-5 text-xs text-gray-600 space-y-1">
            {data.action_taken.map(action => (
              <li key={action}>{action}</li>
            ))}
          </ul>
        </div>
      )}

      {data.instructions?.length > 0 && (
        <div>
          <Text size="xs" fw={600} c="gray.7" className="mb-1">
            Instructions
          </Text>
          <Stack gap={4}>
            {data.instructions.map(instruction => (
              <Group gap="xs" key={instruction} align="flex-start">
                <ChevronRight size={14} className="text-gray-400 mt-0.5" />
                <Text size="xs" c="gray.7">
                  {instruction}
                </Text>
              </Group>
            ))}
          </Stack>
        </div>
      )}

      {nextStageId && (
        <div className="border border-dashed border-gray-200 rounded-md p-3 text-xs text-gray-600">
          <Text fw={600} size="xs" c="gray.7">
            Pending caller response for {nextStageLabel}
          </Text>
          <Text size="xs" c="gray.5">
            New prompts will appear automatically once the caller details are
            captured in the form.
          </Text>
        </div>
      )}
    </Stack>
  )
}

function EmptyState({ dispatcherName }: { dispatcherName?: string }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6">
      <Text fw={600} c="gray.7">
        Waiting for caller details
      </Text>
      <Text size="sm" c="gray.5" mt={4}>
        {dispatcherName
          ? `${dispatcherName}, start entering caller information to receive AI-guided scripts.`
          : 'Start the call by entering caller details to receive AI-guided scripts.'}
      </Text>
    </div>
  )
}

interface UseGuidedProtocolQuestionsArgs {
  callerInformation: Partial<CallerInformationFormValues> | null
  ambulanceInformation: Partial<AmbulanceRequestInformationFormValues> | null
  dispatcherName?: string
  currentSection?: 'caller-information' | 'ambulance-request-information' | null
}

export function useGuidedProtocolQuestions({
  callerInformation,
  ambulanceInformation,
  dispatcherName,
  currentSection,
}: UseGuidedProtocolQuestionsArgs) {
  const dispatchTriage = useDispatchTriage()
  const [stageState, setStageState] = useState<StageRuntimeMap>({})
  const stageStateRef = useRef<StageRuntimeMap>({})
  const [accordionValue, setAccordionValue] =
    useState<DispatchTriageStageId | null>('stage_1_greeting')

  useEffect(() => {
    stageStateRef.current = stageState
  }, [stageState])

  const debouncedCaller = useDebounce(callerInformation, 400)
  const debouncedAmbulance = useDebounce(ambulanceInformation, 400)

  const builderState = useMemo<StageBuilderState>(
    () => ({
      dispatcherName,
      callerInformation: debouncedCaller,
      ambulanceInformation: debouncedAmbulance,
      currentSection,
    }),
    [dispatcherName, debouncedCaller, debouncedAmbulance, currentSection]
  )

  const callStage = useCallback(
    async (
      stageId: DispatchTriageStageId,
      options?: StagePayloadOptions & { force?: boolean }
    ) => {
      const config = STAGE_CONFIG_MAP[stageId]
      if (!config) return
      const payload = config.buildPayload(builderState, options)
      if (!payload) return

      const hash = JSON.stringify(payload)
      const current = stageStateRef.current[stageId]

      const isSamePayload = current?.lastHash === hash
      const alreadySatisfied =
        current?.status === 'success' || current?.status === 'error'

      if (!options?.force && isSamePayload && alreadySatisfied) {
        return
      }

      setStageState(prev => ({
        ...prev,
        [stageId]: {
          ...prev[stageId],
          status: 'loading',
          error: null,
          lastHash: hash,
        },
      }))

      try {
        const response = await dispatchTriage.mutateAsync(payload)
        setStageState(prev => ({
          ...prev,
          [stageId]: {
            status: 'success',
            data: response.data,
            error: null,
            lastHash: hash,
          },
        }))
        setAccordionValue(stageId)
      } catch (error: any) {
        setStageState(prev => ({
          ...prev,
          [stageId]: {
            ...prev[stageId],
            status: 'error',
            error: error?.message || 'Unable to fetch protocol',
            lastHash: hash,
          },
        }))
      }
    },
    [builderState, dispatchTriage]
  )

  useEffect(() => {
    STAGE_CONFIG.forEach(config => {
      if (!config.autoTrigger) return
      const dependencies = config.dependsOn || []
      const dependenciesReady = dependencies.every(
        dep => stageStateRef.current[dep]?.status === 'success'
      )
      if (!dependenciesReady) return
      if (!config.requirement(builderState)) return
      const runtime = stageStateRef.current[config.id]
      if (runtime?.status === 'loading') return
      void callStage(config.id)
    })
  }, [builderState, callStage])

  const stageItems = useMemo<StageItem[]>(() => {
    return STAGE_CONFIG.map(config => {
      const runtime = stageState[config.id] ?? {
        status: config.requirement(builderState) ? 'waiting' : 'idle',
      }
      const dependencyBlocking = config.dependsOn?.find(
        dep => stageState[dep]?.status !== 'success'
      )

      const waitingCopy = dependencyBlocking
        ? `Waiting for ${STAGE_CONFIG_MAP[dependencyBlocking]?.label ?? dependencyBlocking}`
        : !config.requirement(builderState)
          ? config.waitingMessage?.(builderState)
          : undefined

      return {
        config,
        runtime,
        waitingCopy,
      }
    })
  }, [builderState, stageState])

  const retryStage = useCallback(
    (stageId: DispatchTriageStageId) => {
      void callStage(stageId, { force: true })
    },
    [callStage]
  )

  const globalError =
    stageItems.find(stage => stage.runtime.status === 'error')?.runtime.error ||
    undefined

  return {
    stageItems,
    accordionValue,
    setAccordionValue,
    retryStage,
    isMutating: dispatchTriage.isPending,
    globalError,
  }
}

function formatCallerName(state: StageBuilderState) {
  const first = state.callerInformation?.first_name?.trim()
  const last = state.callerInformation?.last_name?.trim()
  if (!first && !last) return undefined
  return [first, last].filter(Boolean).join(' ')
}

function deriveCallReason(
  callerInformation?: Partial<CallerInformationFormValues> | null
) {
  if (!callerInformation) return undefined
  const typeLabel = callerInformation.call_type
    ? CALL_TYPE_LABEL[callerInformation.call_type]
    : undefined
  const categoryLabel = callerInformation.call_category
    ? CALL_CATEGORY_LABEL[callerInformation.call_category]
    : undefined
  if (typeLabel && categoryLabel) {
    return `${typeLabel} - ${categoryLabel}`
  }
  return typeLabel || categoryLabel
}

function deriveLegitimacyIndicator(
  callerInformation?: Partial<CallerInformationFormValues> | null
) {
  if (!callerInformation?.call_type) return 'unknown'
  if (
    callerInformation.call_type === 'emergency' ||
    callerInformation.call_type === 'ambulance_request'
  ) {
    return 'legitimate'
  }
  if (callerInformation.call_type === 'undetermined') {
    return 'needs_review'
  }
  return 'pending_confirmation'
}

function formatDateString(date: Date | string) {
  const parsed = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(parsed.getTime())) return ''
  return format(parsed, 'yyyy-MM-dd')
}

function buildStageContext(
  stageId: DispatchTriageStageId,
  state: StageBuilderState
): Record<string, unknown> {
  const { callerInformation, ambulanceInformation } = state
  const baseGreeting = removeEmpty({
    dispatcher_name: state.dispatcherName,
  })

  switch (stageId) {
    case 'stage_1_greeting':
      return baseGreeting
    case 'stage_2_phone_confirmation':
      return removeEmpty({
        ...buildStageContext('stage_1_greeting', state),
        caller_name: formatCallerName(state),
        caller_phone: callerInformation?.phone_number,
      })
    case 'stage_3_legitimacy_screening':
      return removeEmpty({
        ...buildStageContext('stage_2_phone_confirmation', state),
        confirmed_phone_number: callerInformation?.phone_number,
      })
    case 'stage_4_caller_details': {
      const callerDob = callerInformation?.date_of_birth
        ? formatDateString(callerInformation.date_of_birth)
        : undefined
      return removeEmpty({
        ...buildStageContext('stage_3_legitimacy_screening', state),
        call_reason: deriveCallReason(callerInformation),
        legitimacy_indicator: deriveLegitimacyIndicator(callerInformation),
        caller_prefix: callerInformation?.prefix?.toUpperCase(),
        caller_first_name: callerInformation?.first_name,
        caller_last_name: callerInformation?.last_name,
        caller_dob_or_age: callerDob,
      })
    }
    case 'stage_5_location_capture':
      return removeEmpty({
        ...buildStageContext('stage_4_caller_details', state),
        full_address: callerInformation?.location,
        landmarks: callerInformation?.location,
        nearest_junction_or_busstop: callerInformation?.location,
        google_maps_reference: callerInformation?.location,
      })
    case 'stage_7_immediate_threat_assessment':
      return removeEmpty({
        ...buildStageContext('stage_5_location_capture', state),
        consciousness: mapConsciousness(ambulanceInformation?.is_conscious),
        breathing: mapBreathing(ambulanceInformation),
        bleeding: mapBleeding(ambulanceInformation),
      })
    case 'stage_8_dispatch_confirmation':
      return buildStageContext('stage_7_immediate_threat_assessment', state)
    case 'stage_9_handover_preparation':
      return buildStageContext('stage_8_dispatch_confirmation', state)
    default:
      return baseGreeting
  }
}

function removeEmpty<T extends Record<string, unknown>>(value: T): T {
  const clone = { ...value }
  Object.keys(clone).forEach(key => {
    if (
      clone[key] === undefined ||
      clone[key] === null ||
      clone[key] === '' ||
      (Array.isArray(clone[key]) && (clone[key] as unknown[]).length === 0)
    ) {
      delete clone[key]
    }
  })
  return clone
}

function buildLocationSummary(
  callerInformation?: Partial<CallerInformationFormValues> | null,
  ambulanceInformation?: Partial<AmbulanceRequestInformationFormValues> | null
) {
  const locationParts = [
    callerInformation?.location
      ? `Caller located at ${callerInformation.location}`
      : undefined,
    ambulanceInformation?.pickup_location
      ? `Pickup near ${ambulanceInformation.pickup_location}`
      : undefined,
    ambulanceInformation?.destination
      ? `Destination is ${ambulanceInformation.destination}`
      : undefined,
  ].filter(Boolean)

  return locationParts.join('. ')
}

function buildThreatSummary(
  ambulanceInformation?: Partial<AmbulanceRequestInformationFormValues> | null
) {
  if (!ambulanceInformation) return ''
  const pieces = [
    ambulanceInformation.is_conscious === 'yes'
      ? 'Patient is conscious.'
      : 'Patient is unconscious.',
    ambulanceInformation.oxygen_required === 'yes'
      ? 'Breathing difficulty suspected.'
      : 'Breathing stable.',
    ambulanceInformation.severity_level
      ? `Severity reported as ${ambulanceInformation.severity_level}.`
      : undefined,
  ].filter(Boolean)
  return pieces.join(' ')
}

function mapConsciousness(value?: 'yes' | 'no') {
  if (!value) return undefined
  return value === 'yes' ? 'conscious' : 'unconscious'
}

// Map our limited form fields into the breathing taxonomy expected
// by the triage API. Currently we only know whether oxygen is required,
// so we treat that as a proxy for critical breathing issues.
function mapBreathing(
  ambulanceInformation?: Partial<AmbulanceRequestInformationFormValues> | null
) {
  if (!ambulanceInformation?.oxygen_required) return undefined
  return ambulanceInformation.oxygen_required === 'yes'
    ? 'not_breathing'
    : 'normal'
}

// Severity level is captured as low/moderate/high in the form. Translate that
// into the bleeding enum required by the API so threat assessment still works.
function mapBleeding(
  ambulanceInformation?: Partial<AmbulanceRequestInformationFormValues> | null
) {
  const severity = ambulanceInformation?.severity_level
  if (!severity) return undefined
  if (severity === 'high') return 'heavy'
  if (severity === 'moderate') return 'moderate'
  return 'minor'
}

// Utility that returns the next stage id based on our ordered config list.
// Helpful when the API does not send the next stage in its response.
function getNextStageId(currentStageId: DispatchTriageStageId) {
  const currentIndex = STAGE_CONFIG.findIndex(
    stage => stage.id === currentStageId
  )
  if (currentIndex === -1) return null
  const nextStage = STAGE_CONFIG[currentIndex + 1]
  return nextStage?.id ?? null
}
